import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useFeatureFlag } from "configcat-react";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

// ---------------------------------------------------------------------------
// Experiment registry
// ---------------------------------------------------------------------------

export type ExperimentDefinition = {
    id: string;
    name: string;
    description: string;
};

/**
 * Registry of known experiments. Lifecycle stage and availability are
 * controlled entirely via ConfigCat targeting rules — no `stage` or
 * `availabilityFlag` fields here.
 */
export const KNOWN_EXPERIMENTS = {
    ralph: {
        id: "experiment_ralph",
        name: "Ralph Mode",
        description: "Run Ona in a loop to autonomously build software while you're AFK",
    },
} as const satisfies Record<string, ExperimentDefinition>;

export type ExperimentName = keyof typeof KNOWN_EXPERIMENTS;

/** Typed experiment name constants to avoid string casts. */
export const ExperimentNames: { [K in ExperimentName]: K } = {
    ralph: "ralph",
};

const ALL_EXPERIMENT_NAMES = Object.keys(KNOWN_EXPERIMENTS) as ExperimentName[];

export function isKnownExperiment(name: string): name is ExperimentName {
    return name in KNOWN_EXPERIMENTS;
}

export function getExperiment(name: string): ExperimentDefinition | null {
    if (!isKnownExperiment(name)) {
        return null;
    }
    return KNOWN_EXPERIMENTS[name];
}

// ---------------------------------------------------------------------------
// localStorage helpers — all experiment keys are scoped by org ID
// ---------------------------------------------------------------------------

function experimentToggleKey(name: ExperimentName, orgId: string): string {
    return `experiment_${name}:${orgId}`;
}

function createLocalStorageSubscription(key: string) {
    return (callback: () => void) => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key) {
                callback();
            }
        };
        window.addEventListener("storage", handleStorageChange);
        const handleCustomEvent = () => callback();
        window.addEventListener(`localStorage:${key}`, handleCustomEvent);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(`localStorage:${key}`, handleCustomEvent);
        };
    };
}

function readLocalStorageBool(key: string, defaultValue: boolean): boolean {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return defaultValue;
        return raw === "true";
    } catch {
        return defaultValue;
    }
}

function writeLocalStorage(key: string, value: string) {
    try {
        localStorage.setItem(key, value);
        window.dispatchEvent(new Event(`localStorage:${key}`));
    } catch (e) {
        console.warn(`Failed to write localStorage key ${key}`, e);
    }
}

// ---------------------------------------------------------------------------
// useOrgId — shared helper to get the current org ID for localStorage scoping
// ---------------------------------------------------------------------------

function useOrgId(): string {
    const { data: user } = useAuthenticatedUser();
    return user?.organizationId ?? "";
}

// ---------------------------------------------------------------------------
// useExperimentToggle — per-experiment enabled state (per-org)
// ---------------------------------------------------------------------------

/**
 * Per-experiment toggle. Each experiment has its own key in both localStorage
 * and User Preferences API: `experiment_{name}:{orgId}`.
 * Default is false (opt-in).
 */
export function useExperimentToggle(name: ExperimentName): {
    enabled: boolean;
    setEnabled: (v: boolean) => void;
    loading: boolean;
} {
    const orgId = useOrgId();
    const localStorageKey = experimentToggleKey(name, orgId);
    const { value: remoteValue, setValue: setRemoteValue, isLoading } = usePreferenceValue(localStorageKey);

    const subscribe = useMemo(() => createLocalStorageSubscription(localStorageKey), [localStorageKey]);
    const localEnabled = useSyncExternalStore(
        subscribe,
        () => readLocalStorageBool(localStorageKey, false),
        () => false,
    );

    // Remote wins when defined (cross-device sync), otherwise fall back to local
    const enabled = remoteValue !== undefined ? remoteValue === "true" : localEnabled;

    const setEnabled = useCallback(
        (v: boolean) => {
            if (!orgId) return;
            writeLocalStorage(localStorageKey, String(v));
            setRemoteValue(String(v));
        },
        [orgId, localStorageKey, setRemoteValue],
    );

    return useMemo(() => ({ enabled, setEnabled, loading: isLoading }), [enabled, setEnabled, isLoading]);
}

// ---------------------------------------------------------------------------
// useExperimentsEnabled — derived: true if any experiment is enabled
// ---------------------------------------------------------------------------

/**
 * Returns true if any experiment is currently enabled. Read-only — to
 * enable/disable experiments, use useExperimentToggle for the specific
 * experiment.
 */
export function useExperimentsEnabled(): {
    enabled: boolean;
    loading: boolean;
} {
    // Call all toggle hooks unconditionally (React rules of hooks).
    const ralph = useExperimentToggle("ralph");

    return useMemo(
        () => ({
            enabled: ralph.enabled,
            loading: ralph.loading,
        }),
        [ralph.enabled, ralph.loading],
    );
}

// ---------------------------------------------------------------------------
// useExperimentEnabled — primary hook for components
// ---------------------------------------------------------------------------

/**
 * Returns whether an experiment is active for the current user and org.
 * Passes user state as ConfigCat custom attributes so targeting rules
 * evaluate everything (per-experiment toggle, org/tier) in one flag lookup.
 */
export function useExperimentEnabled(name: ExperimentName): boolean {
    const experiment = KNOWN_EXPERIMENTS[name];
    const { enabled: experimentEnabled } = useExperimentToggle(name);
    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();

    const accountId = account?.id ?? "";
    const accountEmail = account?.email ?? "";
    const orgId = organization?.id ?? "";
    const orgTier = OrganizationTier[organization?.tier ?? OrganizationTier.UNSPECIFIED];

    const user = useMemo(
        () => ({
            identifier: accountId,
            email: accountEmail,
            custom: {
                organization_id: orgId,
                organization_tier: orgTier,
                // TODO(gpl): cleanup after configcat rules have been adjusted
                experiments_enabled: "true",
                // Must match the ConfigCat targeting rule attribute name
                experiment_enabled: String(experimentEnabled),
            },
        }),
        [accountId, accountEmail, orgId, orgTier, experimentEnabled],
    );

    const { value } = useFeatureFlag(experiment.id, false, user);

    return value;
}

// ---------------------------------------------------------------------------
// useExperimentVisibleInSettings — Settings UI visibility probe
// ---------------------------------------------------------------------------

/**
 * Probes ConfigCat to determine if the current user is in the target audience
 * for an experiment, assuming the experiment is enabled.
 *
 * Returns `loading: true` while account/org data is still being fetched so
 * callers can show a skeleton instead of flashing <NotAuthorized />.
 */
export function useExperimentVisibleInSettings(name: ExperimentName): {
    visible: boolean;
    loading: boolean;
} {
    const experiment = KNOWN_EXPERIMENTS[name];
    const { data: account, isLoading: accountLoading } = useGetAccount();
    const { data: organization, isLoading: orgLoading } = useOrganization();

    const accountId = account?.id ?? "";
    const accountEmail = account?.email ?? "";
    const orgId = organization?.id ?? "";
    const orgTier = OrganizationTier[organization?.tier ?? OrganizationTier.UNSPECIFIED];

    const user = useMemo(
        () => ({
            identifier: accountId,
            email: accountEmail,
            custom: {
                organization_id: orgId,
                organization_tier: orgTier,
                // TODO(gpl): cleanup after configcat rules have been adjusted
                experiments_enabled: "true",
                // Must match the ConfigCat targeting rule attribute name
                experiment_enabled: "true",
            },
        }),
        [accountId, accountEmail, orgId, orgTier],
    );

    const { value } = useFeatureFlag(experiment.id, false, user);

    const loading = accountLoading || orgLoading;

    return useMemo(() => ({ visible: loading ? false : value, loading }), [loading, value]);
}

/**
 * Returns true if any experiment is visible in Settings for the current user/org.
 */
export function useAnyExperimentVisibleInSettings(): {
    visible: boolean;
    loading: boolean;
} {
    const ralph = useExperimentVisibleInSettings("ralph");
    return useMemo(() => ({ visible: ralph.visible, loading: ralph.loading }), [ralph.visible, ralph.loading]);
}

// ---------------------------------------------------------------------------
// getEnabledExperimentsMap — synchronous, for Segment enrichment
// ---------------------------------------------------------------------------

/**
 * Synchronous read of all experiment states from localStorage for a given org.
 * Returns a map like `{ ralph: true }` for Segment enrichment.
 * An experiment is enabled when its per-experiment key is "true".
 */
export function getEnabledExperimentsMap(orgId: string | undefined): Record<string, boolean> {
    if (!orgId) return {};

    const map: Record<string, boolean> = {};
    for (const name of ALL_EXPERIMENT_NAMES) {
        if (readLocalStorageBool(experimentToggleKey(name, orgId), false)) {
            map[name] = true;
        }
    }
    return map;
}

// ---------------------------------------------------------------------------
// Migration — v2: per-experiment keys, remote cleanup
// ---------------------------------------------------------------------------

const MIGRATION_V1_FLAG = "experiments_migration_v1";
const MIGRATION_V2_FLAG = "experiments_migration_v2";
const REMOTE_MIGRATION_V2_KEY = "experiments_migration_v2";
const LEGACY_RALPH_KEY = "feature_flag_ralph_mode_enabled";
const EXPERIMENTS_ENABLED_PREFIX = "experiments_enabled:";
const EXPERIMENTS_STATE_PREFIX = "experiments_state:";

/**
 * v2 migration: converts the old global toggle + JSON state map into
 * per-experiment keys.
 *
 * If `experiments_enabled:{orgId}` was "true", the user had opted in —
 * preserve that by writing `experiment_ralph:{orgId} = "true"`.
 *
 * Cleans up all legacy keys from localStorage. Remote cleanup is handled
 * by useRemoteExperimentMigration on first hook load.
 */
export function migrateExperimentState(): void {
    try {
        if (localStorage.getItem(MIGRATION_V2_FLAG)) {
            return;
        }

        // Migrate all orgs that had experiments enabled in one pass
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(EXPERIMENTS_ENABLED_PREFIX)) {
                keysToRemove.push(key);
                if (localStorage.getItem(key) === "true") {
                    const keyOrgId = key.slice(EXPERIMENTS_ENABLED_PREFIX.length);
                    writeLocalStorage(experimentToggleKey(ExperimentNames.ralph, keyOrgId), "true");
                }
            } else if (key?.startsWith(EXPERIMENTS_STATE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        for (const key of keysToRemove) {
            localStorage.removeItem(key);
        }

        // Clean up legacy keys from v1
        localStorage.removeItem(LEGACY_RALPH_KEY);
        localStorage.removeItem(MIGRATION_V1_FLAG);

        localStorage.setItem(MIGRATION_V2_FLAG, "true");
    } catch (e) {
        console.warn("Experiment migration failed", e);
    }
}

/**
 * Hook that runs the one-time experiment migration when the org ID is available.
 * Call this from a component that renders on every authenticated page load.
 */
export function useExperimentMigration(): void {
    const orgId = useOrgId();

    useEffect(() => {
        if (orgId) {
            migrateExperimentState();
        }
    }, [orgId]);
}

/**
 * Hook that handles remote cleanup of stale experiments_enabled preferences
 * and writes the per-experiment remote preference if the user was opted in.
 *
 * Uses a remote migration marker (`experiments_migration_v2`) so the cleanup
 * runs exactly once across all devices.
 */
export function useRemoteExperimentMigration(): void {
    const orgId = useOrgId();
    const oldRemoteKey = `experiments_enabled:${orgId}`;
    const {
        value: oldRemoteValue,
        deleteValue: deleteOldRemote,
        isLoading: isOldLoading,
    } = usePreferenceValue(oldRemoteKey);

    const {
        value: remoteMigrationDone,
        setValue: setRemoteMigrationDone,
        isLoading: isMigrationLoading,
    } = usePreferenceValue(REMOTE_MIGRATION_V2_KEY);

    const ralphKey = experimentToggleKey("ralph", orgId);
    const { setValue: setRemoteRalph } = usePreferenceValue(ralphKey);

    useEffect(() => {
        if (isOldLoading || isMigrationLoading || !orgId) return;
        if (remoteMigrationDone === "true") return;

        // If the user had experiments enabled remotely, write per-experiment key
        if (oldRemoteValue === "true") {
            setRemoteRalph("true");
        }

        // Delete the old remote key
        if (oldRemoteValue !== undefined) {
            deleteOldRemote().catch(console.error);
        }

        setRemoteMigrationDone("true");
    }, [
        isOldLoading,
        isMigrationLoading,
        orgId,
        remoteMigrationDone,
        oldRemoteValue,
        deleteOldRemote,
        setRemoteMigrationDone,
        setRemoteRalph,
    ]);
}

// ---------------------------------------------------------------------------
// Pending experiment activation (for /experiments/:name URL route)
// ---------------------------------------------------------------------------

const PENDING_EXPERIMENT_KEY = "experiment_pending_activation";

/**
 * Store a pending experiment activation to be processed after login/onboarding.
 * The URL route captures the intent; after auth, the per-experiment toggle is set.
 */
export function setPendingExperiment(name: string) {
    if (isKnownExperiment(name)) {
        try {
            localStorage.setItem(PENDING_EXPERIMENT_KEY, name);
        } catch {
            // Ignore localStorage errors
        }
    }
}

export function getPendingExperiment(): string | null {
    try {
        return localStorage.getItem(PENDING_EXPERIMENT_KEY);
    } catch {
        return null;
    }
}

export function clearPendingExperiment() {
    try {
        localStorage.removeItem(PENDING_EXPERIMENT_KEY);
    } catch {
        // Ignore localStorage errors
    }
}
